import { memo, useCallback, useContext, useMemo } from "react";
import "../../css/GlobalStyles.css";
import { useSelector, useDispatch } from "react-redux";
import { LoginState, getAccountState } from "../../redux/slices/accountSlice";
import { Colors } from "../../utils/colors";
import { MARKIT_CONTACT_US, MARKITAI } from "@markit/common.utils";
import { useNavigate } from "../../hooks/useNavigate";
import { DataLoaders } from "../../redux/slices/dataSlice";
import { Icon } from "@iconify/react";
import { NavigationId } from "../../navigation/AppParamList";
import LandingPagesWrapper from "../../components/LandingPage/LandingPagesWrapper";
import { useSlideUpAnimation } from "../../hooks/animationHooks/useSlideUpAnimation";
import { useWindowResize } from "../../hooks/useWindowResize";
import { checkIsDesktop } from "../../utils/screenResponsivenessUtils";
import { MixpanelContext } from "../../context/AnalyticsService";
import TextingThatFeelsHumanFold from "../../components/LandingPage/TextingThatFeelsHumanFold";
import RectangleButton from "../../components/Buttons/RectangleButton";
import { VerticalDivider } from "../../components/Dividers/VerticalDivider";

const PressMediaPage = memo(function PressMediaPageFn() {
  const { account } = useSelector(getAccountState);
  const { accountData, loggedIn } = account;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const mixpanel = useContext(MixpanelContext);

  useSlideUpAnimation();
  const windowWidth = useWindowResize();

  const techstarsArticle =
    "https://www.techstars.com/newsroom/techstars-boston-summer-class-2022-accelerates-interest";

  const isDesktopSize = useMemo(
    () => checkIsDesktop(windowWidth),
    [windowWidth]
  );

  const styles = {
    safeView: {
      paddingInline: isDesktopSize ? 180 : 20,
      paddingBlock: isDesktopSize ? 80 : 60,
      color: Colors.WHITE,
    },
    horizontalBorder: { gap: 60, borderTop: `0.5px solid ${Colors.BLACK1}` },
  };

  const getStartedOnPress = useCallback(
    (btnSource: string) => {
      if (mixpanel) {
        mixpanel.track("Website: Home Pressed Get Started", {
          distinct_id: accountData.uid,
          is_logged_in: loggedIn === LoginState.LOGGED_IN,
          btn_source: btnSource,
        });
      }
      if (loggedIn === LoginState.LOGGED_IN) {
        dispatch(DataLoaders.user(accountData.uid));
        navigate(`/u/${accountData.username}`, {
          state: { username: accountData.username },
        });
      } else {
        navigate(NavigationId.WELCOME);
      }
    },
    [
      accountData.uid,
      accountData.username,
      dispatch,
      loggedIn,
      navigate,
      mixpanel,
    ]
  );

  const renderPressSection = useCallback(
    (title: string, subtext: string, url: string) => (
      <div
        className="ColumnNormal"
        style={{
          flex: 1,
          height: 274,
          borderTop: isDesktopSize ? undefined : `0.5px solid ${Colors.BLACK1}`,
        }}
      >
        <div className="ColumnNormal" style={{ gap: 20, paddingBlock: 60 }}>
          <span style={{ fontSize: 32, fontWeight: 500 }}>{title}</span>
          <span style={{ fontSize: 20, color: Colors.GRAY1 }}>{subtext}</span>
          <div
            className="Centering"
            style={{
              height: 25,
              width: 25,
              borderRadius: 100,
              backgroundColor: isDesktopSize ? Colors.GRAY1 : Colors.WHITE,
              cursor: "pointer",
            }}
            onClick={() => window.open(url)}
            onMouseEnter={(e) =>
              isDesktopSize
                ? (e.currentTarget.style.backgroundColor = Colors.WHITE)
                : undefined
            }
            onMouseLeave={(e) =>
              isDesktopSize
                ? (e.currentTarget.style.backgroundColor = Colors.GRAY1)
                : undefined
            }
          >
            <Icon
              icon="ion:arrow-forward"
              height={14}
              style={{
                color: Colors.BLACK,
                transform: "rotateY(0deg) rotate(-45deg)",
              }}
            />
          </div>
        </div>
      </div>
    ),
    [isDesktopSize]
  );

  const renderVerticalDivider = useMemo(
    () => (
      <VerticalDivider height={274} color={Colors.BLACK1} thickness={0.5} />
    ),
    []
  );

  const pressItems = useMemo(
    () => [
      {
        title: "The Boston Globe",
        subtext:
          "Jonathan Chang, chief executive of Markit raises funding round from Techstars and pitches at Techstars Demo Day.",
        url: "https://www.bostonglobe.com/2022/08/11/business/our-favorite-moments-techstars-boston-demo-day/",
      },
      {
        title: "Techstars",
        subtext:
          "Markit has built a platform to connect independent artists and performers with their audiences.",
        url: techstarsArticle,
      },
      {
        title: "MGMT Boston",
        subtext:
          "Jonathan Chang and team are on a mission to reinvent engagement and events for creators and Gen-Z.",
        url: "https://mgmtboston.substack.com/p/mgmt-boston-w12-q2-23-markit-social",
      },
      {
        title: "Tufts 100K",
        subtext: "Winner of the 2022 Tufts $100k New Ventures Competition",
        url: "https://gordon.tufts.edu/news-events/news/annual-100k-new-ventures-competition-drives-entrepreneurial-mindset-winning-teams-announced",
      },
      {
        title: "CEO Monthly",
        subtext:
          "Most Influential CEO 2023 in Boston, building the future of fan engagement with texting.",
        url: "https://www.ceo-review.com/winners/markit-social-inc/",
      },
      {
        title: "The Gordon Institute",
        subtext:
          "\"Creators use Markit to promote to new audiences and engage their previous event attendees. It's fun, it's social and makes finding things to do simple\"",
        url: "https://gordon.tufts.edu/news-events/news/jumbos-showcase-ventures-techstars-boston-accelerator-demo-day",
      },
    ],
    [techstarsArticle]
  );

  const renderPressItems = useMemo(() => {
    if (isDesktopSize) {
      const rows = [];
      for (let i = 0; i < pressItems.length; i += 2) {
        rows.push(
          <div key={i} className="AlignedRow" style={styles.horizontalBorder}>
            {renderPressSection(
              pressItems[i].title,
              pressItems[i].subtext,
              pressItems[i].url
            )}
            {renderVerticalDivider}
            {pressItems[i + 1] &&
              renderPressSection(
                pressItems[i + 1].title,
                pressItems[i + 1].subtext,
                pressItems[i + 1].url
              )}
          </div>
        );
      }
      return (
        <div className="ColumnNormal" style={{ minWidth: 1152 }}>
          {rows}
        </div>
      );
    } else {
      return (
        <div className="ColumnNormal">
          {pressItems.map((item) =>
            renderPressSection(item.title, item.subtext, item.url)
          )}
        </div>
      );
    }
  }, [
    isDesktopSize,
    pressItems,
    renderPressSection,
    renderVerticalDivider,
    styles.horizontalBorder,
  ]);

  return (
    <LandingPagesWrapper
      helmetData={{
        title: "Press and Media | Markit AI",
        metaContent:
          "Markit AI in the news. See how Markit AI is being used by creators and brands to engage with their audience.",
        link: MARKITAI + "/press",
      }}
      noRedirect
    >
      <div
        className="ColumnNormal"
        style={{ ...styles.safeView, overflowX: "auto" }}
      >
        <div className="ColumnNormal" style={{ gap: isDesktopSize ? 60 : 40 }}>
          <div
            className="ColumnNormal"
            style={{
              gap: isDesktopSize ? 40 : 30,
              width: isDesktopSize ? 640 : undefined,
            }}
          >
            <div className="ColumnNormal" style={{ gap: 24 }}>
              <div className="ColumnNormal" style={{ gap: 10 }}>
                <div
                  className="AlignedRowSelect"
                  onClick={() => window.open(techstarsArticle)}
                  style={{
                    gap: 4,
                    padding: "10px 14px",
                    borderRadius: 100,
                    border: `0.5px solid ${Colors.BLACK1}`,
                    width: "fit-content",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.7")}
                  onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
                >
                  <span
                    className={isDesktopSize ? "sectionTitle" : "bodySubtext"}
                    style={{ color: Colors.GRAY1 }}
                  >
                    Backed by{" "}
                    <span style={{ color: Colors.GREEN2 }}>Techstars</span>
                  </span>
                  <Icon
                    icon="ion:chevron-forward"
                    height={14}
                    style={{ color: Colors.GREEN2, marginBottom: -1 }}
                  />
                </div>
                <span
                  style={{ fontSize: isDesktopSize ? 64 : 48, fontWeight: 500 }}
                >
                  Press and Media
                </span>
              </div>
              <span
                style={{
                  fontSize: 18,
                  color: Colors.GRAY1,
                  lineHeight: "24px",
                }}
              >
                Markit AI is a Boston-based venture-backed startup that has
                raised $1.8M to help creators monetize their content off social
                media through text messaging.
                {isDesktopSize ? <br /> : " "}
                For all media and press inquiries, please contact our team.
              </span>
            </div>
            <RectangleButton
              buttonLabel="Get in Touch"
              iconRight={
                <Icon
                  icon="ion:chevron-forward"
                  height={14}
                  style={{ color: Colors.BLACK }}
                />
              }
              onPress={() => window.open(MARKIT_CONTACT_US)}
              altColor={Colors.WHITE}
              altTextColor={Colors.BLACK}
              containerStyles={{
                padding: "12px 16px",
                borderRadius: 10,
                width: "fit-content",
              }}
            />
          </div>
          {renderPressItems}
        </div>
      </div>
      <TextingThatFeelsHumanFold
        isDesktopSize={isDesktopSize}
        getStartedOnPress={getStartedOnPress}
      />
    </LandingPagesWrapper>
  );
});

export default PressMediaPage;
